/**
 * Mapa canónico: cta_type de /admin/cta-por-region → render del hero en BaseTemplate*.
 *
 * Tipos admin (WishlistAdmin CtaType + legacy):
 *   none | birthday | wishlist | community | checkout | mejores_amigos | dual*
 *
 * * dual en AR se normaliza a community en api/cta-flags y Worker.
 *
 * Prioridad de render (misma cascada que resolveActiveHeroCtaType):
 *   hideCta → admin_override → checkout → community → mejores_amigos/birthday+url → birthday → wishlist
 */

import type { TFunction } from "i18next";

import type { HeroCtaResolvedType } from "@/lib/heroCtaFunnel";

/** Tipos persistidos en wishlist_geo_config (espejo WishlistAdmin). */
export const ADMIN_CTA_TYPES = [
  "none",
  "birthday",
  "wishlist",
  "community",
  "checkout",
  "mejores_amigos",
  "dual",
] as const;

export type AdminCtaType = (typeof ADMIN_CTA_TYPES)[number];

/** Hrefs por defecto cuando el admin no setea url override. */
export const HERO_CTA_DEFAULT_HREFS = {
  community: "/invitacion-premium",
  wishlist: "/fan/wishlist",
  birthday: "/r/cumple",
  checkoutIntl: "/checkout-channel-invite",
} as const;

export type HeroCtaRenderKind =
  | "checkout"
  | "community"
  | "mejores_amigos"
  | "birthday"
  | "wishlist";

/**
 * Labels fijos en español para admin (espejo i18n del visitante en BaseTemplate*).
 * Community: `baseTemplate.black.heroCtaLabelCommunity` en es.json — no el label corto del picker.
 */
export const HERO_CTA_VISITOR_LABELS_ES: Record<HeroCtaRenderKind, string> = {
  checkout: "Ser Exclusivo",
  community: "Únete a mi comunidad",
  mejores_amigos: "Mejores Amigos",
  birthday: "Cumpleaños",
  wishlist: "Wishlist",
};

/**
 * Texto operador en /admin/cta-por-region — destinos reales del hero (no rutas legacy).
 * Mejores Amigos y Ser Exclusivo AR usan URL MP del tier elegido o env preapproval.
 */
export const ADMIN_CTA_DESTINATION_HINTS: Record<
  AdminCtaType,
  { label: string; href: string; note?: string }
> = {
  none: { label: "Sin botón", href: "—" },
  wishlist: {
    label: "Wishlist",
    href: HERO_CTA_DEFAULT_HREFS.wishlist,
    note: "botón deshabilitado en hero",
  },
  birthday: { label: "Cumpleaños", href: HERO_CTA_DEFAULT_HREFS.birthday },
  community: {
    label: "Comunidad",
    href: HERO_CTA_DEFAULT_HREFS.community,
    note: "label=VIP text (default Miembro Exclusivo); URL MP; Mux geo",
  },
  checkout: {
    label: "Ser Exclusivo",
    href: "MercadoPago (AR) / Stripe (intl)",
    note: "ctaUrl admin o plan preapproval",
  },
  mejores_amigos: {
    label: "Mejores Amigos",
    href: "URL MercadoPago (tier Precio Medio/Alto)",
    note: "guardada en wishlist_geo_config.url",
  },
  dual: {
    label: "Comunidad",
    href: HERO_CTA_DEFAULT_HREFS.community,
    note: "AR: dual → community",
  },
};

/** Forma mínima de GET /api/cta-flags para preview admin. */
export interface CtaFlagsLike {
  hideCta: boolean;
  showCheckout: boolean;
  showCommunity: boolean;
  showBirthday: boolean;
  showWishlist: boolean;
  showBestFriends?: boolean;
  ctaUrl?: string | null;
  ctaLabel?: string | null;
}

export interface HeroCtaFlagsInput {
  hideCta: boolean;
  showCtaSkeleton: boolean;
  hasAdminHeroCta?: boolean;
  showCheckout: boolean;
  hasCheckoutRoute: boolean;
  showCommunity: boolean;
  showBestFriends: boolean;
  showBirthday: boolean;
  showWishlist: boolean;
  ctaUrl?: string;
}

/** Variante concreta a renderizar en el hero (null = sin CTA). */
export function resolveHeroCtaRenderKind(
  input: HeroCtaFlagsInput,
): HeroCtaRenderKind | null {
  if (input.hideCta || input.showCtaSkeleton || input.hasAdminHeroCta) {
    return null;
  }
  if (input.showCheckout && input.hasCheckoutRoute) return "checkout";
  if (input.showCommunity) return "community";
  if (input.showBestFriends || (input.showBirthday && Boolean(input.ctaUrl?.trim()))) {
    return "mejores_amigos";
  }
  if (input.showBirthday) return "birthday";
  if (input.showWishlist) return "wishlist";
  return null;
}

/** Tipo para Slack/PostHog (espejo de flags activos). */
export function resolveHeroCtaResolvedType(
  input: HeroCtaFlagsInput,
): HeroCtaResolvedType | null {
  if (input.hideCta || input.showCtaSkeleton) return null;
  if (input.hasAdminHeroCta) return "admin_override";
  const kind = resolveHeroCtaRenderKind({
    ...input,
    hasAdminHeroCta: false,
  });
  if (!kind) return null;
  if (kind === "birthday") return "birthday";
  return kind;
}

export function isExternalHeroCtaHref(href: string): boolean {
  return /^https?:\/\//i.test(href.trim());
}

/**
 * Community hero href.
 * Community = landing `/invitacion-premium` (video + CTA VIP MercadoPago).
 * El pago VIP AR vive en esa landing (MP), NO en el hero.
 * Si admin/Worker pegó URL MP → se ignora aquí (va a DualCTA como vipHref geo).
 * Si pegó Stripe / checkout externo → también se ignora (nunca hero Stripe en AR).
 * Solo se aceptan rutas internas relativas (ej. /invitacion-premium?…).
 */
export function resolveCommunityHeroHref(
  ctaUrl?: string,
  _opts?: { isArgentina?: boolean; arVipMpUrl?: string },
): string {
  const u = ctaUrl?.trim();
  if (
    u &&
    u.startsWith("/") &&
    !/^https?:\/\//i.test(u) &&
    !/mercadopago\.com/i.test(u) &&
    !/preapproval_plan_id=/i.test(u) &&
    !/stripe\.com|buy\.stripe\.com|checkout\.stripe\.com/i.test(u)
  ) {
    return u;
  }
  return HERO_CTA_DEFAULT_HREFS.community;
}

export function resolveBirthdayHeroHref(): string {
  return HERO_CTA_DEFAULT_HREFS.birthday;
}

export function resolveWishlistHeroHref(): string {
  return HERO_CTA_DEFAULT_HREFS.wishlist;
}

export interface HeroCtaCheckoutHrefInput {
  ctaUrl?: string;
  isArgentina: boolean;
  arPrecreatedMpUrl?: string;
}

/** Checkout: ctaUrl admin → MP precreated (AR) → Stripe route (intl). */
export function resolveCheckoutHeroHref(input: HeroCtaCheckoutHrefInput): string {
  const override = input.ctaUrl?.trim();
  if (override) return override;
  if (input.isArgentina) return input.arPrecreatedMpUrl?.trim() || "";
  return HERO_CTA_DEFAULT_HREFS.checkoutIntl;
}

export interface HeroCtaLabelResolveInput {
  kind: HeroCtaRenderKind;
  t: TFunction;
  ctaLabel?: string;
}

/** Labels: checkout y community aceptan `ctaLabel` admin; resto i18n fijo. */
export function resolveHeroCtaDisplayLabel(input: HeroCtaLabelResolveInput): string {
  const { kind, t, ctaLabel } = input;
  const custom = ctaLabel?.trim() || "";
  switch (kind) {
    case "checkout":
      return (
        custom ||
        t("baseTemplate.black.heroCtaLabelCheckout", { defaultValue: "Ser Exclusivo" })
      );
    case "community":
      // Override geo (cta-por-región label). Vacío → i18n comunidad.
      return (
        custom ||
        t("baseTemplate.black.heroCtaLabelCommunity")
      );
    case "mejores_amigos":
      return t("baseTemplate.heroCtaLabelBestFriends");
    case "birthday":
      return t("baseTemplate.black.heroCtaLabelBirthday");
    case "wishlist":
      return t("baseTemplate.black.heroCtaLabelWishlist");
    default:
      return "";
  }
}

export function resolveHeroCtaAriaLabel(
  kind: HeroCtaRenderKind,
  t: TFunction,
): string {
  switch (kind) {
    case "checkout":
      return t("baseTemplate.black.heroCtaAriaCheckout", {
        defaultValue: "Ser Exclusivo",
      });
    case "community":
      return t("baseTemplate.black.heroCtaAriaCommunity");
    case "mejores_amigos":
      return t("baseTemplate.heroCtaLabelBestFriends");
    case "birthday":
      return t("baseTemplate.black.heroCtaAriaBirthday");
    case "wishlist":
      return t("baseTemplate.black.heroCtaAriaWishlist");
    default:
      return "";
  }
}

export function resolveHeroCtaElementId(kind: HeroCtaRenderKind): string {
  switch (kind) {
    case "checkout":
      return "hero_cta_checkout";
    case "community":
      return "hero_cta_community";
    case "mejores_amigos":
      return "hero_cta_mejores_amigos";
    case "birthday":
      return "hero_cta_birthday";
    case "wishlist":
      return "hero_cta_wishlist";
    default:
      return "hero_cta_unknown";
  }
}

/** Href final por variante (para impresiones Slack / analytics). */
export function resolveHeroCtaHrefForKind(
  kind: HeroCtaRenderKind,
  opts: {
    ctaUrl?: string;
    checkoutRoute?: string;
    isArgentina?: boolean;
    arVipMpUrl?: string;
  },
): string {
  switch (kind) {
    case "checkout":
      return opts.checkoutRoute?.trim() || HERO_CTA_DEFAULT_HREFS.checkoutIntl;
    case "community":
      return resolveCommunityHeroHref(opts.ctaUrl, {
        isArgentina: opts.isArgentina,
        arVipMpUrl: opts.arVipMpUrl,
      });
    case "mejores_amigos":
      return opts.ctaUrl?.trim() || "";
    case "birthday":
      return resolveBirthdayHeroHref();
    case "wishlist":
      return resolveWishlistHeroHref();
    default:
      return "";
  }
}

/**
 * Preview visitante desde respuesta cta-flags — misma prioridad que BaseTemplate*.
 * Usado por CtaResolutionPreview y docs admin.
 */
export function resolveVisitorHeroPreviewFromCtaFlags(flags: CtaFlagsLike): {
  kind: HeroCtaRenderKind | null;
  label: string;
  href: string;
  hrefIsPlaceholder: boolean;
} {
  if (flags.hideCta) {
    return { kind: null, label: "Sin botón", href: "—", hrefIsPlaceholder: false };
  }

  const kind = resolveHeroCtaRenderKind({
    hideCta: false,
    showCtaSkeleton: false,
    hasAdminHeroCta: false,
    showCheckout: flags.showCheckout,
    hasCheckoutRoute: flags.showCheckout,
    showCommunity: flags.showCommunity,
    showBestFriends: !!flags.showBestFriends,
    showBirthday: flags.showBirthday,
    showWishlist: flags.showWishlist,
    ctaUrl: flags.ctaUrl ?? undefined,
  });

  if (!kind) {
    return { kind: null, label: "Sin botón", href: "—", hrefIsPlaceholder: false };
  }

  let href = resolveHeroCtaHrefForKind(kind, {
    ctaUrl: flags.ctaUrl ?? undefined,
    checkoutRoute: flags.ctaUrl?.trim() || undefined,
  });

  let hrefIsPlaceholder = false;
  if (kind === "checkout" && !href) {
    href = "MercadoPago (plan preapproval del env)";
    hrefIsPlaceholder = true;
  }
  if (kind === "mejores_amigos" && !href) {
    href = "Requiere URL MP (tier Precio Medio/Alto)";
    hrefIsPlaceholder = true;
  }

  let label = HERO_CTA_VISITOR_LABELS_ES[kind];
  if (kind === "checkout" && flags.ctaLabel?.trim()) {
    label = flags.ctaLabel.trim();
  }
  if (kind === "wishlist") {
    label = "Wishlist (deshabilitado)";
  }

  return { kind, label, href, hrefIsPlaceholder };
}

/** Documentación operador: qué flags activa cada cta_type en api/cta-flags. */
export const ADMIN_CTA_TO_FLAGS: Record<
  AdminCtaType,
  {
    hideCta: boolean;
    showCheckout: boolean;
    showCommunity: boolean;
    showBirthday: boolean;
    showWishlist: boolean;
    showBestFriends: boolean;
    requiresUrl: boolean;
    usesCustomLabel: boolean;
    defaultHref: string | null;
  }
> = {
  none: {
    hideCta: true,
    showCheckout: false,
    showCommunity: false,
    showBirthday: false,
    showWishlist: false,
    showBestFriends: false,
    requiresUrl: false,
    usesCustomLabel: false,
    defaultHref: null,
  },
  checkout: {
    hideCta: false,
    showCheckout: true,
    showCommunity: false,
    showBirthday: false,
    showWishlist: false,
    showBestFriends: false,
    requiresUrl: false,
    usesCustomLabel: true,
    defaultHref: null,
  },
  community: {
    hideCta: false,
    showCheckout: false,
    showCommunity: true,
    showBirthday: false,
    showWishlist: false,
    showBestFriends: false,
    requiresUrl: false,
    usesCustomLabel: true,
    defaultHref: HERO_CTA_DEFAULT_HREFS.community,
  },
  birthday: {
    hideCta: false,
    showCheckout: false,
    showCommunity: false,
    showBirthday: true,
    showWishlist: false,
    showBestFriends: false,
    requiresUrl: false,
    usesCustomLabel: false,
    defaultHref: HERO_CTA_DEFAULT_HREFS.birthday,
  },
  mejores_amigos: {
    hideCta: false,
    showCheckout: false,
    showCommunity: false,
    showBirthday: false,
    showWishlist: false,
    showBestFriends: true,
    requiresUrl: true,
    usesCustomLabel: false,
    defaultHref: null,
  },
  wishlist: {
    hideCta: false,
    showCheckout: false,
    showCommunity: false,
    showBirthday: false,
    showWishlist: true,
    showBestFriends: false,
    requiresUrl: false,
    usesCustomLabel: false,
    defaultHref: HERO_CTA_DEFAULT_HREFS.wishlist,
  },
  dual: {
    hideCta: false,
    showCheckout: false,
    showCommunity: true,
    showBirthday: false,
    showWishlist: false,
    showBestFriends: false,
    requiresUrl: false,
    usesCustomLabel: false,
    defaultHref: HERO_CTA_DEFAULT_HREFS.community,
  },
};